import {
  getActionBadgeStyle,
  getStatusBadgeStyle,
  formatResourceDisplay,
  formatTimestamp,
} from "@utils/auditFormatters";

// Derive status from response_code
function getStatus(responseCode) {
  if (!responseCode) return "UNKNOWN";
  return responseCode >= 200 && responseCode < 300 ? "SUCCESS" : "FAILURE";
}

// Format action for display (normalize enum string)
function formatAction(action) {
  if (!action) return "UNKNOWN";
  // Handle "ActionType.CREATE" format from backend
  const cleanAction = String(action).replace(/^ActionType\./, "").toUpperCase();
  return cleanAction;
}

function ActionBadge({ action }) {
  const displayAction = formatAction(action);
  const badgeStyle = getActionBadgeStyle(displayAction);
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${badgeStyle}`}
    >
      {displayAction}
    </span>
  );
}

function StatusBadge({ responseCode }) {
  const status = getStatus(responseCode);
  const badgeStyle = getStatusBadgeStyle(status);
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${badgeStyle}`}
    >
      {status}
    </span>
  );
}

function AuditLogCard({ log }) {
  return (
    <div className="rounded-2xl border bg-white p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 truncate">
            {log.user_id ? `User ${log.user_id.slice(0, 8)}...` : "Unknown User"}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {formatTimestamp(log.timestamp)}
          </div>
        </div>
        <ActionBadge action={log.action} />
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-slate-600">Resource:</span>
          <span className="text-slate-900 font-medium">
            {formatResourceDisplay(log.resource_type, log.resource_id)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-600">IP Address:</span>
          <span className="text-slate-900 font-mono text-xs">
            {log.ip_address || "-"}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-slate-600">Status:</span>
          <StatusBadge responseCode={log.response_code} />
        </div>
      </div>
    </div>
  );
}

export default function AuditLogsTable({
  logs,
  loading,
  error,
  total,
  onExportCSV,
  onExportJSON,
  exporting,
}) {
  const safeLogs = logs || [];
  const empty = !loading && !error && safeLogs.length === 0;

  return (
    <div className="flex-1 min-h-0 overflow-hidden rounded-2xl border bg-white flex flex-col">
      <div className="px-5 py-3 border-b flex items-center justify-between">
        <div className="font-semibold text-slate-900 flex items-center gap-2">
          <span className="p-2 rounded-xl bg-slate-50 border">
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </span>
          Audit Logs
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-slate-500">
            Showing{" "}
            <span className="font-semibold text-slate-900">
              {safeLogs.length}
            </span>{" "}
            / <span className="font-semibold text-slate-900">{total || 0}</span>
          </div>
          {/* Export Buttons */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onExportCSV}
              disabled={loading || exporting === "csv" || safeLogs.length === 0}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-slate-50 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Export as CSV"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {exporting === "csv" ? "Exporting…" : "CSV"}
            </button>
            <button
              type="button"
              onClick={onExportJSON}
              disabled={
                loading || exporting === "json" || safeLogs.length === 0
              }
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border bg-white hover:bg-slate-50 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Export as JSON"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {exporting === "json" ? "Exporting…" : "JSON"}
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="p-5 text-slate-600">Loading audit logs…</div>}
      {error && <div className="p-5 text-red-600">{error}</div>}

      {empty && (
        <div className="p-10 text-center">
          <div className="mx-auto w-fit p-4 rounded-2xl bg-gradient-to-br from-sky-500/15 via-purple-500/15 to-emerald-500/15 border">
            <svg
              className="w-7 h-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <div className="mt-3 font-semibold text-slate-900">
            No audit logs found
          </div>
          <div className="text-sm text-slate-600 mt-1">
            Try adjusting your filters or date range.
          </div>
        </div>
      )}

      {!loading && !error && safeLogs.length > 0 && (
        <>
          {/* Mobile cards */}
          <div className="md:hidden p-3 space-y-3 overflow-y-auto">
            {safeLogs.map((log) => (
              <AuditLogCard key={log.id} log={log} />
            ))}
          </div>

          {/* Tablet/Desktop table */}
          <div className="hidden md:block overflow-auto flex-1">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 sticky top-0 bg-white">
                <tr className="border-b">
                  <th className="p-3">Timestamp</th>
                  <th className="p-3">User</th>
                  <th className="p-3">Action</th>
                  <th className="p-3">Resource</th>
                  <th className="p-3">IP Address</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>

              <tbody>
                {safeLogs.map((log) => (
                  <tr
                    key={log.id}
                    className="border-b hover:bg-slate-50/70 transition-colors"
                  >
                    <td className="p-3 text-slate-700">
                      {formatTimestamp(log.timestamp)}
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-slate-900">
                        {log.user_id ? `${log.user_id.slice(0, 8)}...` : "Unknown"}
                      </div>
                    </td>
                    <td className="p-3">
                      <ActionBadge action={log.action} />
                    </td>
                    <td className="p-3 text-slate-700">
                      {formatResourceDisplay(log.resource_type, log.resource_id)}
                    </td>
                    <td className="p-3 font-mono text-xs text-slate-600">
                      {log.ip_address || "-"}
                    </td>
                    <td className="p-3">
                      <StatusBadge responseCode={log.response_code} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="p-4 text-xs text-slate-500 border-t">
              Tip: Use the export buttons above to download audit logs as CSV
              or JSON.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
